/*
 * PathDetail — the "look before you commit" page for a session path.
 * Opened from a Home path card, BEFORE session setup: shows exactly what the
 * path covers (sub-areas, question counts, the learner's own mastery), then
 * hands off to Session Settings with the path pre-selected. If the current
 * saved session already lives inside this path, it becomes the session's
 * detail view instead: Resume + View session details.
 */
import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/path.css';
import '../../styles/memory.css';          // reuse .mem-chip / .mem-bar
import APIService from '../../services/APIService';
import * as sessionStore from './sessionStore';
import { findPath, getHubTreeCached, indexTree, leavesOf, rollupCount, sessionWithinPath } from './paths';

const STATUS = {
  new: ['New', 'new'], learning: ['Learning', 'learning'], weak: ['Weak', 'weak'],
  fading: ['Fading', 'fading'], strong: ['Strong', 'strong'],
};
const chip = (s) => STATUS[s] || STATUS.new;
const pct = (v) => `${Math.round((v || 0) * 100)}%`;

const PathDetail = ({ pathId, session, onBack, onOpenSettings, onResume, onViewPlan }) => {
  const [hub, setHub] = useState(null);
  const [mem, setMem] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setError('');
    Promise.all([getHubTreeCached(), APIService.getMemoryTree().catch(() => ({}))])
      .then(([h, m]) => { if (alive) { setHub(h.tree || []); setMem(m); } })
      .catch((e) => { if (alive) setError(e.message || 'Could not load this path.'); });
    return () => { alive = false; };
  }, [pathId]);

  const path = useMemo(() => (hub ? findPath(hub, pathId) : null), [hub, pathId]);
  const hubById = useMemo(() => indexTree(hub || []), [hub]);
  const memById = useMemo(() => indexTree(mem?.tree || []), [mem]);
  const isCurrent = path && sessionWithinPath(session, path);

  // Rows: for one-root areas, the root's children; for role bundles, each root
  // becomes a group heading over its children.
  const groups = useMemo(() => {
    if (!path) return [];
    return path.rootIds.map((rid) => {
      const root = hubById[rid];
      if (!root) return null;
      const rows = (root.children && root.children.length ? root.children : [root]).map((c) => {
        const m = memById[c.id];
        return {
          id: c.id, name: c.name, count: rollupCount(c),
          mastery: m && m.attempts > 0 ? m.mastery : null,
          status: m && m.attempts > 0 ? m.status : 'new',
          attempts: m?.attempts || 0,
        };
      });
      return { id: rid, name: root.name, rows };
    }).filter(Boolean);
  }, [path, hubById, memById]);

  const stats = useMemo(() => {
    const rows = groups.flatMap((g) => g.rows);
    const touched = rows.filter((r) => r.attempts > 0);
    const att = touched.reduce((n, r) => n + r.attempts, 0);
    return {
      questions: path ? path.count : 0,
      areas: rows.length,
      touchedPct: rows.length ? Math.round((touched.length / rows.length) * 100) : 0,
      mastery: att > 0 ? touched.reduce((n, r) => n + (r.mastery || 0) * r.attempts, 0) / att : null,
      weak: rows.filter((r) => r.status === 'weak' || r.status === 'fading').length,
    };
  }, [groups, path]);

  const startPath = () => {
    const leafIds = path.rootIds.flatMap((rid) => leavesOf(hubById[rid]).map((n) => n.id));
    const draft = sessionStore.loadDraft() || {};
    sessionStore.saveDraft({ ...draft, sel: leafIds });
    onOpenSettings();
  };

  if (error) {
    return (
      <div className="pd-wrap"><div className="pd-center">
        <p className="pd-err">{error}</p>
        <button className="hm-ghost" type="button" onClick={onBack}>← Back to Home</button>
      </div></div>
    );
  }
  if (!hub) return <div className="pd-wrap"><div className="pd-center">Loading this path…</div></div>;
  if (!path) {
    return (
      <div className="pd-wrap"><div className="pd-center">
        <p>This path isn't in your knowledge base any more.</p>
        <button className="hm-ghost" type="button" onClick={onBack}>← Back to Home</button>
      </div></div>
    );
  }

  return (
    <div className="pd-wrap" style={{ '--pc': path.accent }}>
      <button className="pd-back" type="button" onClick={onBack}>← Home</button>

      <header className="pd-head">
        <div>
          <div className="pd-kind">{path.kind === 'role' ? 'Role path' : 'Area path'}</div>
          <h1>{path.title}</h1>
          <p className="pd-blurb">{path.blurb}</p>
        </div>
        <div className="pd-actions">
          {isCurrent ? (
            <>
              <div className="pd-current">● This is your current session{session.status === 'planned' ? ' (planned)' : ''}</div>
              <button className="hm-primary" type="button" onClick={onResume}>
                {session.status === 'planned' ? 'Review plan →' : 'Resume session →'}
              </button>
              {session.plan && (
                <button className="hm-ghost" type="button" onClick={onViewPlan}>View session details</button>
              )}
            </>
          ) : (
            <button className="hm-primary" type="button" onClick={startPath}>
              Set up a session with this path →
            </button>
          )}
        </div>
      </header>

      <div className="pd-stats">
        <div><b>{stats.questions.toLocaleString()}</b><span>questions</span></div>
        <div><b>{stats.areas}</b><span>sub-areas</span></div>
        <div><b>{stats.touchedPct}%</b><span>started</span></div>
        <div><b>{stats.mastery != null ? pct(stats.mastery) : '—'}</b><span>your mastery</span></div>
        <div><b>{stats.weak}</b><span>need work</span></div>
      </div>

      <section className="pd-covered">
        <h2>What this path covers</h2>
        {groups.map((g) => (
          <div key={g.id} className="pd-group">
            {path.rootIds.length > 1 && <div className="pd-group-h">{g.name}</div>}
            {g.rows.map((r) => (
              <div key={r.id} className="pd-row">
                <span className="pd-row-name">{r.name}</span>
                <span className="pd-row-count">{r.count.toLocaleString()} q</span>
                <span className="mem-bar" role="img" aria-label={`Mastery ${pct(r.mastery)}`}>
                  <i className={chip(r.status)[1]} style={{ width: r.mastery != null ? pct(r.mastery) : '0%' }} />
                </span>
                <span className={`mem-chip ${chip(r.status)[1]}`}>{chip(r.status)[0]}</span>
              </div>
            ))}
          </div>
        ))}
      </section>

      <p className="pd-foot">
        Starting a session pre-selects all of this in Session Settings — trim topics or
        tune difficulty there, then the agent builds your plan with reasons.
      </p>
    </div>
  );
};

export default PathDetail;
